import React, { useState } from "react";
import ReactDOM from "react-dom";
import { Check } from "lucide-react";
import { t } from "../../utils/i18n";
import { dateActivityByKey } from "../../utils/dateActivities";
import PayRuleCallout from "./PayRuleCallout";
import type { DateInvitation } from "../../hooks/useDateState";

// Fiche de réponse à une invitation de rendez-vous — ouverte depuis le profil (bouton "Répondre")
// ou depuis une carte de notification. Même coquille que les autres sheets Date.
export default function DateResponseSheet({
  lang, invitation, onAccept, onDecline, onClose,
}: {
  lang: string; invitation: DateInvitation;
  onAccept: () => Promise<any>; onDecline: () => Promise<any>; onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const activity = dateActivityByKey(invitation.activity_key);
  const formattedDate = new Intl.DateTimeFormat(lang, { weekday: "long", day: "numeric", month: "long" }).format(new Date(`${invitation.scheduled_date}T00:00:00`));

  const handleAccept = async () => {
    setBusy(true);
    const data = await onAccept();
    setBusy(false);
    if (data?.state === "confirmed") {
      setAccepted(true);
      setTimeout(onClose, 1400);
    }
  };
  const handleDecline = async () => {
    setBusy(true);
    await onDecline();
    setBusy(false);
    onClose();
  };

  // Portail direct sur document.body — même bug/fix que PhotoLightbox.tsx : sur Safari iOS un
  // position:fixed descendant du conteneur mobile WebkitOverflowScrolling:"touch" de Layout.tsx
  // se positionne par rapport à tout son contenu scrollable plutôt qu'au vrai viewport.
  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 3000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", maxWidth: 480, maxHeight: "88vh", overflowY: "auto", boxSizing: "border-box" }}>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
          <button onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999", padding: 0, lineHeight: 1 }}>✕</button>
        </div>

        {accepted ? (
          <div style={{ textAlign: "center", padding: "30px 0" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <Check size={26} color="white" />
            </div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{t(lang, "date_action_accept_confirm")}</div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 19, fontWeight: 800, textAlign: "center", marginBottom: 18 }}>{t(lang, "date_recap_title")}</div>
            <div style={{ backgroundColor: "#fafafa", borderRadius: 16, padding: "4px 14px", marginBottom: 16 }}>
              {activity && (
                <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #F0F0F0" }}>
                  <activity.Icon size={17} color="#0a0a0a" />
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>{t(lang, activity.i18nKey)}</div>
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #F0F0F0" }}>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>{invitation.place_name}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #F0F0F0" }}>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>{formattedDate}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0" }}>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>{invitation.scheduled_time}</div>
              </div>
            </div>
            <PayRuleCallout lang={lang} variant="invitee" />
            <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
              <button onClick={handleDecline} disabled={busy} style={{ flex: 1, padding: "13px", borderRadius: 14, border: "1px solid #E0E0E0", backgroundColor: "white", color: "#0a0a0a", fontSize: 13.5, fontWeight: 700, cursor: busy ? "default" : "pointer" }}>
                {t(lang, "date_action_decline")}
              </button>
              <button onClick={handleAccept} disabled={busy} style={{ flex: 1, padding: "13px", borderRadius: 14, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 13.5, fontWeight: 700, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
                {t(lang, "date_action_accept")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
